import { useEffect, useRef } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';

import { useAppDispatch } from '../state-manager/store';
import { prepareMainUserAction } from '../state-manager/thunks/usersThunks';
import { useSelector } from 'react-redux';
import {
  getUserIdSelector,
  getRequestStatusSelector,
} from '../state-manager/selectors/appSelectors';
import { isFailed } from '../state-manager/utils/requestStatus';
import { notify } from '../state-manager/slices/notificationsSlice';
import { LoginService } from '../services/loginService';
import { trackPortalEvent } from '../observability/rum/rumClient';
import { PORTAL_EVENTS } from '../observability/rum/rumEvents';

export function useOnAuthenticatorChange() {
  const dispatch = useAppDispatch();
  const { user: amplifyUser } = useAuthenticator((context) => [context.user]);
  // Emit auth.login_succeeded once per app load (amplifyUser changes on token
  // refresh, which we don't want to count as a new login).
  const loginTracked = useRef(false);

  useEffect(() => {
    if (!amplifyUser) {
      return;
    }
    const loginService = LoginService.getInstance();
    loginService.setAmplifyUser(amplifyUser);
    const userId = loginService.getUserId();
    const role = loginService.getUserRole();
    if (!userId) {
      throw new Error('missing userId');
    }
    if (!role) {
      throw new Error('missing role');
    }
    dispatch(prepareMainUserAction(userId, role));
    if (!loginTracked.current) {
      loginTracked.current = true;
      trackPortalEvent(PORTAL_EVENTS.AUTH_LOGIN_SUCCEEDED, { outcome: 'success' });
    }
  }, [amplifyUser]);
}

export function useHandlePrepareMainUserFailed() {
  const dispatch = useAppDispatch();
  const userId = useSelector(getUserIdSelector);
  const requestStatus = useSelector(getRequestStatusSelector('user'));

  useEffect(() => {
    if (!userId) {
      return;
    }
    if (isFailed({ requestStatus, id: userId })) {
      dispatch(
        notify({
          message: 'Oops something went wrong - please contact customers service',
          severity: 'error',
          origin: {
            vertical: 'top',
            horizontal: 'center',
          },
        }),
      );
    }
  }, [requestStatus, userId]);
}
